import { TFunction } from "i18next";
import { range } from "lodash-es";
import moment from "moment-timezone";
import { v4 } from "uuid";
import {
  scheduleAutobook,
  scheduleRoosterLink
} from "../../services/api/schedule.api";
import { RoosterLinkSchedulePayload } from "../../services/api/types/schedule.roosterlink.type";
import { HostType, SchedulerStoreType } from "../../state/scheduler.store";
import { BookingFormType, LegalType } from "../../types";

const getBookedSlotAsPreviousSlot = (
  sessionData: SchedulerStoreType["sessionData"],
  duration?: number
) => {
  const selectedSlot = sessionData?.selectedSlot;
  if (
    !selectedSlot ||
    selectedSlot.hour == null ||
    selectedSlot.minute == null ||
    !sessionData?.selectedTimezone
  ) {
    return undefined;
  }

  return [
    {
      day: moment(selectedSlot.date).tz("UTC", true).startOf("day").valueOf(),
      hour: selectedSlot.hour,
      minute: selectedSlot.minute,
      timeZone: sessionData.selectedTimezone,
      ...(duration ? { duration } : {})
    }
  ];
};

export const scheduleAutobookHelper = async ({
  availabilityId,
  sessionData,
  meetingLink,
  actions
}: {
  sessionData: SchedulerStoreType["sessionData"];
  meetingLink: SchedulerStoreType["meetingLink"];
  availabilityId: string;
  actions: SchedulerStoreType["actions"];
}) => {
  if (
    sessionData?.selectedTimezone &&
    sessionData?.selectedSlot?.date &&
    sessionData?.selectedSlot?.hour != null &&
    sessionData?.selectedSlot?.minute != null
  ) {
    actions.setUiState({ currentStep: "scheduling", currentView: "month" });

    const result = await scheduleAutobook({
      availabilityId,
      timeZone: sessionData?.selectedTimezone,
      slot: {
        participants: sessionData?.selectedSlot?.participants,
        day: moment(sessionData?.selectedSlot?.date).tz("UTC", true).valueOf(),
        slots: [
          {
            hour: sessionData?.selectedSlot?.hour,
            minute: sessionData?.selectedSlot?.minute
          }
        ]
      },
      mode: sessionData.isRescheduleFlow ? "reschedule" : "schedule",
      hasDynamicFilters: meetingLink?.hasDynamicInterviewersFilter ?? false,
      reason: sessionData?.rescheduleReason?.reason,
      comment: sessionData?.rescheduleReason?.comment
    });
    if (result === "OK") {
      const prevSelectedSlot = getBookedSlotAsPreviousSlot(
        sessionData,
        meetingLink?.availability?.duration
      );
      if (prevSelectedSlot) {
        actions.setSessionData({
          ...sessionData,
          // Snapshot the slot rescheduled FROM (pre-overwrite) for the
          // confirmation screen; prevSelectedSlot below becomes the just-booked
          // slot as the optimistic baseline for the next same-page reschedule.
          confirmationPrevSlot: sessionData?.prevSelectedSlot ?? [],
          prevSelectedSlot
        });
      }
      actions.setUiState({ currentStep: "scheduled", currentView: "month" });
    } else {
      actions.setUiState({
        currentStep: "scheduling_failed",
        currentView: "month"
      });
    }
    return result;
  }
};

export const scheduleRoosterLinkHelper = async ({
  meetingLink,
  sessionData,
  providers,
  link,
  host,
  formData,
  actions,
  ui,
  type,
  legal,
  t
}: {
  formData: BookingFormType;
  host: HostType;
  meetingLink: SchedulerStoreType["meetingLink"];
  sessionData: SchedulerStoreType["sessionData"];
  providers: SchedulerStoreType["providers"];
  link: SchedulerStoreType["link"];
  actions: SchedulerStoreType["actions"];
  ui: SchedulerStoreType["ui"];
  type: "schedule" | "reschedule";
  // The gated T&C list (see useLegalTerms): empty for an internal user, the
  // tenant's terms for a candidate. The server stamps dateTimeAccepted on each
  // entry and persists it as consent on the interview doc, so this MUST be the
  // gated value, not the raw meetingLink terms — otherwise an internal
  // reschedule records an acceptance the UI never presented (R495-405).
  legal: LegalType[];
  t: TFunction;
}) => {
  let payload: RoosterLinkSchedulePayload | undefined;

  if (
    meetingLink?.scheduledData?.docDataRoosterLinks?.clientId &&
    sessionData?.selectedSlot &&
    sessionData?.selectedSlot?.hour !== null &&
    sessionData?.selectedSlot?.minute !== null &&
    sessionData?.selectedTimezone &&
    link?.userId &&
    link?.linkId
  ) {
    const applicationId =
      type === "schedule"
        ? v4()
        : meetingLink.scheduledData?.docDataRoosterLinks.parent;
    actions.setUiState({ currentStep: "scheduling", currentView: "month" });

    const availabilityDocPayload = {
      cancel: meetingLink?.scheduledData?.docDataRoosterLinks?.cancel ?? false,
      candidateEmailTemplate:
        meetingLink.scheduledData.docDataRoosterLinks.candidateEmailTemplate,
      clientId:
        meetingLink.scheduledData?.docDataRoosterLinks.client ??
        meetingLink?.scheduledData?.docDataRoosterLinks.clientId,
      conferenceType:
        meetingLink.scheduledData.docDataRoosterLinks.conferenceType,
      created: moment().utc().toDate(),
      customUrl: false,
      days: range(0, 7).map((i) => ({ day: i, start: 0, end: 1440 })),
      duration: meetingLink.availability?.duration ?? 30,
      end: meetingLink.availability?.availabilityWindowMins ?? 0,
      id: meetingLink.scheduledData.docDataRoosterLinks.id,
      interviewerEmailTemplate:
        meetingLink.scheduledData.docDataRoosterLinks.interviewerEmailTemplate,
      interviewers: meetingLink.scheduledData.docDataRoosterLinks.interviewers,
      minutesAfter: meetingLink.availability?.availabilityWindowMins ?? 0,
      minutesBefore: 0,
      parent: meetingLink.scheduledData.docDataRoosterLinks.parent,
      profileType: meetingLink.scheduledData.docDataRoosterLinks.profileType,
      reschedule:
        meetingLink?.scheduledData?.docDataRoosterLinks?.reschedule ?? false,
      schedule: true,
      start: meetingLink.scheduledData.docDataRoosterLinks.start,
      timeZone: meetingLink.scheduledData.docDataRoosterLinks.timeZone,
      updated: moment().utc().toDate(),
      userId: meetingLink.scheduledData.docDataRoosterLinks.userId,
      workerId: meetingLink.scheduledData.docDataRoosterLinks.workerId
    };

    payload = {
      availabilityDoc: availabilityDocPayload,
      legal:
        legal?.map((l) => ({
          id: l?.parentId,
          type: l?.type,
          termsAndConditionId: l?._id,
          version: l?.version,
          contentId: l?.content?.[0]?._id ?? "",
          localeCode: l?.content?.[0]?.language ?? ""
        })) ?? [],
      schedule: {
        id: type === "schedule" ? "schedule" : (link?.scheduleId ?? ""),
        action: type === "schedule" ? "schedule" : "reschedule",
        reason: null,
        day: moment(sessionData?.selectedSlot?.date)
          .tz("UTC", true)
          .startOf("day")
          .valueOf(),
        hour: sessionData?.selectedSlot?.hour,
        minute: sessionData?.selectedSlot?.minute,
        timeZone: sessionData?.selectedTimezone,
        applicationId
      },
      prospectsForm: formData,
      linkMetaInfo: {
        ...availabilityDocPayload,
        hostEmail: meetingLink.roosterLinksMeta?.email ?? host?.email ?? "",
        showTentativeAsBusy: meetingLink.showTentativeAsBusy ?? true,
        organizer: meetingLink.roosterLinksMeta?.organizer,
        additionalInterviewersInfo:
          meetingLink.scheduledData.docDataRoosterLinks
            .additionalInterviewersInfo ?? []
      },
      conferencing:
        meetingLink?.interviewers?.map((i) => {
          return {
            type: "interviewer",
            value: i.email ?? i.value,
            provider:
              providers?.find((p) => p.email === i.email || p.email === i.value)
                ?.provider ?? ""
          };
        }) ?? [],
      availabilityQuery: {
        considerFreeFormAvailabilityWithinRange: ui?.currentView === "week",
        queryWindows:
          meetingLink?.availability?.range?.map((range) => ({
            timeMin: range.rangeStart,
            timeMax: range.rangeEnd
          })) ?? [],
        responseTZ: sessionData?.selectedTimezone ?? "",
        isProspect: meetingLink?.roosterLinksMeta?.profileType === "prospect",
        ...(host?.interviewerPreferences
          ? {
              overriddenInterviewerPreferences: {
                [host.email]: host.interviewerPreferences ?? {}
              }
            }
          : {})
      }
    };

    try {
      const result = await scheduleRoosterLink(
        link?.userId,
        link?.linkId,
        payload
      );
      if (result?.scheduled === true) {
        const prevSelectedSlot = getBookedSlotAsPreviousSlot(
          sessionData,
          meetingLink.availability?.duration
        );
        if (prevSelectedSlot) {
          actions.setSessionData({
            ...sessionData,
            // `sessionData` is the render-time snapshot captured before
            // handleFormInput stored the entered form — carry `formData`
            // through so the confirmation screen keeps the candidate name
            // instead of "Undefined Undefined" (R495-278).
            formData,
            // Snapshot the slot rescheduled FROM (pre-overwrite) for the
            // confirmation screen; prevSelectedSlot below becomes the
            // just-booked slot as the optimistic baseline for the next
            // same-page reschedule.
            confirmationPrevSlot: sessionData?.prevSelectedSlot ?? [],
            prevSelectedSlot
          });
        }
        actions.setUiState({ currentStep: "scheduled", currentView: "month" });
        return result;
      }

      if (result?.errorCode === "SLOT_NOT_AVAILABLE") {
        actions.setUiState({
          currentStep: "scheduling_failed",
          currentView: "month",
          meetingCardError: {
            code: result.errorCode,
            message: t("ROOSTER.PUBLIC-APP.SLOT_NOT_AVAILABLE", {
              defaultValue: "Selected slot is not available."
            })
          }
        });
        return result;
      }

      if (!result.scheduled) {
        throw Error("UNKNOWN_ERROR");
      }

      return result;
    } catch (e: unknown) {
      console.error("Error scheduling rooster link", e);
      actions.setUiState({
        currentStep: "scheduling_failed",
        currentView: "month"
      });
    }
  }
};
